import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithGoogle } from '../../lib/auth';
import { useAuth } from '../../contexts/AuthContext';
import { EXTERNAL_LOGIN_DENIED_MESSAGE } from '../../lib/internalLogin';

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, userRole, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // 檢查用戶是否已登入但沒有角色
  useEffect(() => {
    if (user && !userRole && !authLoading) {
      setError(EXTERNAL_LOGIN_DENIED_MESSAGE);
    }
  }, [user, userRole, authLoading]);

  // 登入成功後，等待 userRole 載入並導航
  useEffect(() => {
    if (user && userRole && !authLoading) {
      // 清除導航超時（如果存在）
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
      
      // 根據角色導航到對應的儀表板
      let targetRoute = '/login';
      if (userRole.role === 'admin' || userRole.role === 'operator') {
        targetRoute = '/admin/dashboard';
      }
      
      if (targetRoute !== '/login') {
        setLoading(false); // 導航前清除 loading 狀態
        navigate(targetRoute, { replace: true });
      }
    }
  }, [user, userRole, authLoading, navigate]);

  // 清理超時（組件卸載時）
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
    };
  }, []);

  // 移除這裡的 onAuthStateChange 監聽器
  // 因為 App.tsx 中的路由邏輯已經處理了重定向
  // 這裡的監聽器會與 AuthContext 的狀態管理衝突，導致組件快速消失

  const handleGoogleLogin = async (e?: React.MouseEvent) => {
    // 防止表單提交
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setLoading(true);
    setError('');

    // 設置超時保護：如果 30 秒後還沒有完成，重置 loading 狀態
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setError('登入超時，請檢查：\n1. 彈出視窗是否被瀏覽器阻止\n2. 網路連線是否正常\n3. Firebase 配置是否正確');
      // eslint-disable-next-line no-console
      console.warn('⚠️ 登入超時（30秒）');
    }, 30000);

    try {
      await signInWithGoogle(true);
      
      // 登入成功，清除超時
      clearTimeout(timeoutId);
      
      // 登入成功，保持 loading 狀態，等待 userRole 載入
      // userRole 載入後，useEffect 會自動導航
      // 設置超時保護：如果 15 秒後還沒有導航，重置 loading 狀態並顯示提示
      navigationTimeoutRef.current = setTimeout(() => {
        setLoading(false);
        // eslint-disable-next-line no-console
        console.warn('⚠️ 登入成功但 15 秒後仍未導航，可能是 userRole 載入失敗');
        // eslint-disable-next-line no-console
        console.warn('⚠️ 當前狀態', {
          hasUser: !!user,
          hasUserRole: !!userRole,
          authLoading
        });
        
        // 檢查具體原因
        if (user && !userRole && !authLoading) {
          setError(EXTERNAL_LOGIN_DENIED_MESSAGE);
        } else if (user && !userRole && authLoading) {
          setError('登入成功，但角色資訊載入超時。請重新整理頁面重試。');
        }
      }, 15000);
      
    } catch (error: unknown) {
      // 清除超時
      clearTimeout(timeoutId);
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
      
      const firebaseError = error as { code?: string; message?: string };
      // eslint-disable-next-line no-console
      console.error('❌ 登入錯誤:', firebaseError);
      
      // 處理 Popup 被阻止的情況
      if (firebaseError.code === 'auth/popup-blocked') {
        setError('登入視窗被瀏覽器阻止。請：\n1. 允許此網站的彈出視窗\n2. 重新整理頁面後重試\n\n或者系統將自動使用重定向方式登入...');
        // 自動嘗試使用 redirect 方式
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else if (firebaseError.code === 'auth/popup-closed-by-user') {
        setError('登入視窗已關閉。請重新嘗試登入。');
      } else if (firebaseError.message?.includes('Cross-Origin-Opener-Policy')) {
        // COOP 錯誤通常是警告，不影響登入
        // eslint-disable-next-line no-console
        console.warn('⚠️ COOP 警告（通常不影響登入）:', firebaseError.message);
        // 不清除 loading，讓登入流程繼續
        // 如果登入實際成功，user 和 userRole 會更新，useEffect 會處理導航
        // 設置一個較短的超時，如果 5 秒後還沒有 user，則顯示錯誤
        setTimeout(() => {
          if (!user) {
            setLoading(false);
            setError('登入過程中發生問題，請重新嘗試。');
          }
        }, 5000);
        return;
      } else if (firebaseError.code === 'auth/unauthorized-domain') {
        setError('未授權的網域。請確認：\n1. Firebase Authentication 授權域名包含 localhost\n2. OAuth Client ID 的 Authorized JavaScript origins 包含 http://localhost:3000\n詳見：.cursor/FIREBASE_LOGIN_TROUBLESHOOTING.md');
      } else if (firebaseError.code === 'auth/operation-not-allowed') {
        setError('OAuth 提供者未啟用。請在 Firebase Console 中啟用 Google 登入提供者。');
      } else {
        setError(`登入時發生錯誤：${firebaseError.message || firebaseError.code || '未知錯誤'}\n錯誤代碼：${firebaseError.code || 'N/A'}`);
      }
      
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ci-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-6">
            <img 
              src="/Logo-with-name.svg" 
              alt="Teleworker CRM" 
              className="h-16 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-ci-text">
            登入您的帳戶
          </h2>
          <p className="mt-2 text-center text-sm text-ci-text">
            公司營運管理系統
          </p>
        </div>
        
        <div className="mt-8 space-y-6">
          {error && (
            <div className="text-ci-highlight text-sm text-center bg-ci-highlight/20 p-3 rounded-md">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-ci-text bg-ci-button hover:bg-ci-button/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ci-text mr-2"></div>
                  登入中...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  使用 Google 帳號登入
                </div>
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-ci-text">
              登入即表示您同意我們的服務條款和隱私政策
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 